/**
 * VideoPlayerWithAdvertisingSupport Component
 * Version: 1.3.11
 * 
 * Changelog v1.3.11:
 * - CORRECTION MAJEURE : Utilisation de useMemo pour overlayContent
 * - Les dimensions (adWidth, marginHorizontal, marginVertical) sont maintenant des dépendances
 * - Force le re-render de l'advertisement quand les dimensions changent
 * - Ajout de logs détaillés pour le recalcul de overlayContent
 * - Key de l'Advertisement inclut maintenant adWidth pour forcer le re-render
 * 
 * Changelog v1.3.10:
 * - DEBUG : Ajout de logs détaillés avec numéro de version [1.3.10]
 * - Logs au montage du composant
 * - Logs à chaque appel de updateDimensions avec toutes les valeurs calculées
 * - Logs lors du ResizeObserver trigger
 * - Logs lors des événements fullscreen
 * - Logs lors du rendu de l'advertisement avec ses styles appliqués
 * - Permet de diagnostiquer pourquoi l'ads reste petit en plein écran
 */

import   React           , 
       { useEffect       ,
         useState        ,
         useRef          }  from 'react';

import { ErrorData           }  from 'hls.js';

import   classNames             from 'classnames';

import   styles                 from './VideoPlayerWithAdvertisingSupport.module.scss';

import { Advertisement       }  from './Advertisement';
import   VideoPlayer            from '../../widget/players/VideoPlayer';

import { Video               }  from '../../../dataTypes/mediaManagment/video_types';
import { Product             }  from '../../../dataTypes/productManagment/types';
import { SalesAction         }  from '../../../dataTypes/salesCampaignManagment/types';
import { timeStringToSeconds }  from '../../../dataTypes/timeManagment/utils';

import { getSalesActions     }  from '../../../backendServices/salesCampaignManagment/api_salesCampaign';
import { getProducts         }  from '../../../backendServices/productManagment/api_product';
import { getImageById        ,
         downloadImageBlob    }  from '../../../backendServices/mediaManagment/api_image';
import { WWW_URLS            }  from '../../../backendServices/urls';


type VideoPlayerWithAdvertisingSupportProps = {
  video               : Video;
  onReady?            : () => void;
  onStart?            : () => void;
  onError?            : (error: ErrorData) => void;
  onProgress?         : (progress: number) => void;
  onEnd?              : () => void;
  onPause?            : () => void;
  onSeek?             : (currentTime: number) => void;
  onGoToStart?        : (currentTime: number) => void;
  onGoToEnd?          : (endTime: number, startTime: number, duration: number) => void;
  className?          : string;
  existingMarkers?    : Array <{
                                 start : number;
                                 end   : number;
                                 id?   : number;
                              }>;
  currentMarker?      : {
                          start: number;
                          end: number;
                        } | null;
					 
  currentSalesActionRef? : React.MutableRefObject<SalesAction | undefined>;
  enableAdvertisements?  : boolean;
};

const VideoPlayerWithAdvertisingSupport = ({ video                     ,
                                             onReady                   ,
                                             onStart                   ,
                                             onError                   ,
                                             onProgress                ,
                                             onEnd                     ,
                                             onPause                   ,
                                             onSeek                    ,
                                             onGoToStart               ,
                                             onGoToEnd                 ,
                                             className                 ,
                                             existingMarkers = []      ,
                                             currentMarker   = null    ,
											 currentSalesActionRef     ,
                                             enableAdvertisements = true }: VideoPlayerWithAdvertisingSupportProps) => {

  const [ products          , setProducts          ] = useState<Product[]>([]);
  const [ salesActions      , setSalesActions      ] = useState<SalesAction[]>([]);
  
  const [ currentProduct    , setCurrentProduct    ] = useState<Product | null | undefined>(null);

  const [ adPanelPosition   , setAdPanelPosition   ] = useState("");
  const [ showAdPanel       , setShowAdPanel       ] = useState<SalesAction | null>(null);

  const [ url               , setUrl               ] = useState(`${WWW_URLS.MediaServer}${video.publicRef}/videos/001/index.m3u8`);
  const [ alternateImageUrl , setAlternateImageUrl ] = useState<string|undefined>(undefined);

  const [ adWidth          , setAdWidth            ] = useState<number>( 160 );
  const [ marginHorizontal , setMarginHorizontal   ] = useState<number>( 10 );
  const [ marginVertical   , setMarginVertical     ] = useState<number>( 10 );


  const containerRef = useRef<HTMLDivElement>(null);

  // ✅ AJOUT v1.3.1 : État pour forcer le re-render de l'Advertisement
  const [adUpdateCounter, setAdUpdateCounter] = useState(0);

  useEffect(() => {

    console.log ('=========================== currentSalesActionRef :', currentSalesActionRef?.current)
  }, []);

  // Chargement des données (produits et actions commerciales autres que l'action courante
  useEffect(() => {
    if (!enableAdvertisements) {
      setUrl(`${WWW_URLS.MediaServer}${video.publicRef}/videos/001/index.m3u8`);
      return;
    }

    const fetchData = async () => {
      try 
	   {
        const salesActionsResult = await getSalesActions(video.id);

        if (salesActionsResult.payload) 
		 {
          setSalesActions(salesActionsResult.payload);
         }

        const productsResult = await getProducts();

        if (productsResult.payload) 
		 {
          setProducts(productsResult.payload);
         }
       } 
	  catch (error) 
	   {
        console.error('Error fetching data:', error);
       }
    };

    fetchData();

    setUrl(`${WWW_URLS.MediaServer}${video.publicRef}/videos/001/index.m3u8`);
	
  }, [video.id, video.uuid, enableAdvertisements]);


  const loadImageBlob = async () => {

    try
     {
	  if ( showAdPanel )
	   {
        const getImageByIdResult = await getImageById( showAdPanel.alternateImageId);

        if (getImageByIdResult.code === 1)
         {
          const completeImageStruct = await downloadImageBlob ( getImageByIdResult.payload                    ,
                                                                // (progress) => { setImageProgress(progress); } 
		  													  );

          if (completeImageStruct.objectURL)
                {
			     console.log (completeImageStruct.objectURL)	  
				    
   	   	         setAlternateImageUrl (`${completeImageStruct.objectURL}`)
                }
           else {
                 console.error("objectURL is undefined");
                }
         }
       }
	 }
    catch (error)
     {
      console.error("Erreur lors du chargement de l'image:", error);
     }
  };

  // Mise à jour du produit courant et de la position du panneau publicitaire
  useEffect(() => {
    if (!enableAdvertisements) {
      return;
    }

    const actionToUse = (currentSalesActionRef?.current && showAdPanel?.id === currentSalesActionRef.current.id) 
      ? currentSalesActionRef.current 
      : showAdPanel;

    if (actionToUse) {
      const product = products.find((product) => product.id === actionToUse.productId);
      setCurrentProduct(product || null);
      setAdPanelPosition(actionToUse.adPosition === 'right' ? 'right' : 'left');
      
      if (actionToUse.alternateImageId != null && actionToUse.alternateImageId !== 0) {
        loadImageBlob();
      } else if (actionToUse.alternateImageUrl) {
        setAlternateImageUrl(actionToUse.alternateImageUrl);
      }
    } else {
      setCurrentProduct(null);
    }
  }, [showAdPanel, products, currentSalesActionRef?.current, enableAdvertisements]);

  // ✅ AJOUT v1.3.1 : Surveiller les changements dans currentSalesActionRef pour forcer la mise à jour
  useEffect(() => {
    if (!enableAdvertisements) {
      return;
    }

    if (currentSalesActionRef?.current) {
      console.log('🔄 VideoPlayerWithAds: currentSalesActionRef changed, forcing ad update');
      setAdUpdateCounter(prev => prev + 1);
      
      // Mettre à jour le produit même sans showAdPanel
      const product = products.find((p) => p.id === currentSalesActionRef.current!.productId);
      if (product && product.id !== currentProduct?.id) {
        setCurrentProduct(product);
      }
      
      // Mettre à jour la position
      const position = currentSalesActionRef.current.adPosition === 'right' ? 'right' : 'left';
      if (position !== adPanelPosition) {
        setAdPanelPosition(position);
      }
      
      // Mettre à jour l'image alternative
      if (currentSalesActionRef.current.alternateImageUrl !== alternateImageUrl) {
        setAlternateImageUrl(currentSalesActionRef.current.alternateImageUrl ?? undefined);
      }
    }
  }, [
    currentSalesActionRef?.current?.productId,
    currentSalesActionRef?.current?.productVariant,
    currentSalesActionRef?.current?.productImage,
    currentSalesActionRef?.current?.timecode,
    currentSalesActionRef?.current?.duration,
    currentSalesActionRef?.current?.alternateImageUrl,
    currentSalesActionRef?.current?.adPosition,
    currentSalesActionRef?.current?.onlyQRcode,
    currentSalesActionRef?.current?.comment1,
    currentSalesActionRef?.current?.comment2,
    currentSalesActionRef?.current?.shapeColor1,
    currentSalesActionRef?.current?.shapeColor2,
    currentSalesActionRef?.current?.backgroundColor1,
    currentSalesActionRef?.current?.backgroundColor2,
    currentSalesActionRef?.current?.withImage,
    currentSalesActionRef?.current?.redirectionUuid,
    products,
    enableAdvertisements
  ]);

  useEffect(() => {
    if (!enableAdvertisements) {
      return;
    }

    if (currentSalesActionRef?.current && showAdPanel?.id === currentSalesActionRef.current.id) {
      const updatedProduct = products.find((product) => product.id === currentSalesActionRef.current!.productId);
      if (updatedProduct && updatedProduct.id !== currentProduct?.id) {
        setCurrentProduct(updatedProduct);
      }
      
      const updatedPosition = currentSalesActionRef.current.adPosition === 'right' ? 'right' : 'left';
      if (updatedPosition !== adPanelPosition) {
        setAdPanelPosition(updatedPosition);
      }
      
      if (currentSalesActionRef.current.alternateImageUrl !== alternateImageUrl) {
        setAlternateImageUrl(currentSalesActionRef.current.alternateImageUrl ?? undefined);
      }
    }
  }, [currentSalesActionRef?.current, showAdPanel, products, currentProduct, adPanelPosition, alternateImageUrl, enableAdvertisements]);

  useEffect(() => {
    if (!enableAdvertisements) {
      return;
    }

    if (currentSalesActionRef?.current) {
      const videoElement = document.querySelector('video');
      if (videoElement) {
        const currentAction = getCurrentAction(videoElement.currentTime);
        if (currentAction?.id !== showAdPanel?.id || 
            currentAction?.timecode !== showAdPanel?.timecode ||
            currentAction?.duration !== showAdPanel?.duration) {
          setShowAdPanel(currentAction);
        }
      }
    }
  }, [
    currentSalesActionRef?.current?.timecode, 
    currentSalesActionRef?.current?.duration,
    currentSalesActionRef?.current?.alternateImageUrl,
    currentSalesActionRef?.current?.productId,
    currentSalesActionRef?.current?.productVariant,
    currentSalesActionRef?.current?.productImage,
    enableAdvertisements
  ]);


  
  const getCurrentAction = (currentTime: number): SalesAction | null => {
    if (!enableAdvertisements) {
      return null;
    }
	  
    if (currentSalesActionRef?.current) {
      if (!currentSalesActionRef.current.timecode || !currentSalesActionRef.current.duration) {
        return null;
      }
      
      const actionStart = timeStringToSeconds(currentSalesActionRef.current.timecode);
      const actionEnd = actionStart + timeStringToSeconds(currentSalesActionRef.current.duration);
	  
	  if (currentTime >= actionStart && currentTime <= actionEnd) {
		return currentSalesActionRef.current;   
	  }
	}

    if (salesActions.length === 0) {
      return null;
    }

    return (salesActions.find((action) => {
      const actionStart = timeStringToSeconds(action.timecode);
      const actionEnd = actionStart + timeStringToSeconds(action.duration);
      return currentTime >= actionStart && currentTime <= actionEnd;
    }) || null);
  };

  const handleProgress = (currentTime: number) => {
    if (enableAdvertisements) {
      const currentAction = getCurrentAction(currentTime);
      setShowAdPanel(currentAction);
    }
    if (onProgress) onProgress(currentTime);
  };

  const handleSeek = (currentTime: number) => {
    if (enableAdvertisements) {
      const currentAction = getCurrentAction(currentTime);
      setShowAdPanel(currentAction);
    }
    if (onSeek) onSeek(currentTime);
  };

  const handleGoToStart = (currentTime: number) => {
    if (enableAdvertisements) {
      const currentAction = getCurrentAction(currentTime);
      setShowAdPanel(currentAction);
    }
    if (onGoToStart) onGoToStart(currentTime);
  };

  const handleGoToEnd = (endTime: number, startTime: number, duration: number) => {
    if (enableAdvertisements) {
      const currentAction = getCurrentAction(endTime);
      setShowAdPanel(currentAction);
    }
    if (onGoToEnd) onGoToEnd(endTime, startTime, duration);
  };

  // ✅ REFONTE v1.3.6 : Dimensions et position basées uniquement sur l'élément <video>
  const [, setVideoRect] = useState({ width: 0, height: 0, left: 0, top: 0 });

  // ✅ v1.3.11 : Observer les changements de taille + événements fullscreen avec logs détaillés
  useEffect(() => {
    const VERSION = '1.3.11';
    
    const updateDimensions = () => {
      console.log(`[${VERSION}] 🔍 updateDimensions called`);
      
      if (!containerRef.current) {
        console.log(`[${VERSION}] ❌ containerRef.current is null`);
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      console.log(`[${VERSION}] 📦 Container rect:`, {
        width: containerRect.width,
        height: containerRect.height,
        left: containerRect.left,
        top: containerRect.top
      });
      
      const videoElement = containerRef.current.querySelector('video');
      
      if (!videoElement) {
        console.log(`[${VERSION}] ❌ videoElement not found`);
        return;
      }

      const videoRect = videoElement.getBoundingClientRect();
      console.log(`[${VERSION}] 🎬 Video rect:`, {
        width: videoRect.width,
        height: videoRect.height,
        left: videoRect.left,
        top: videoRect.top
      });
      
      // Calculer les dimensions proportionnelles basées sur la taille réelle de la vidéo
      const adWidth          = videoRect.width  > 0 ? (videoRect.width  * 250) / 1920 : 160;
      const marginHorizontal = videoRect.width  > 0 ? (videoRect.width  *  10) / 1920 :  10;
      const marginVertical   = videoRect.height > 0 ? (videoRect.height *  10) / 1080 :  10;
      
      console.log(`[${VERSION}] 📊 Calculated dimensions:`, {
        adWidth,
        marginHorizontal,
        marginVertical,
        calculation: {
          adWidthFormula: `(${videoRect.width} * 250) / 1920 = ${adWidth}`,
          marginHFormula: `(${videoRect.width} * 10) / 1920 = ${marginHorizontal}`,
          marginVFormula: `(${videoRect.height} * 10) / 1080 = ${marginVertical}`
        }
      });
	  
	  setAdWidth          ( adWidth );
      setMarginHorizontal ( marginHorizontal );
      setMarginVertical   ( marginVertical );
      
      setVideoRect({
        width: videoRect.width,
        height: videoRect.height,
        left: videoRect.left,
        top: videoRect.top
      });

      console.log(`[${VERSION}] ✅ State updated with new dimensions`);
      console.log(`[${VERSION}] 📐 Full summary:`, {
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
        videoWidth: videoRect.width,
        videoHeight: videoRect.height,
        adWidth,
        marginHorizontal,
        marginVertical,
        isFullscreen: !!(document.fullscreenElement)
      });
    };

    console.log(`[${VERSION}] 🚀 VideoPlayerWithAdvertisingSupport mounted`);
    
    // Mise à jour initiale
    updateDimensions();
    
    // Observer les changements de taille du container
    const resizeObserver = new ResizeObserver(() => {
      console.log(`[${VERSION}] 🔄 ResizeObserver triggered`);
      updateDimensions();
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      console.log(`[${VERSION}] 👀 Observing container`);
    }

    // Observer aussi les changements de taille de l'élément video
    const videoElement = containerRef.current?.querySelector('video');
    if (videoElement) {
      resizeObserver.observe(videoElement);
      videoElement.addEventListener('loadedmetadata', updateDimensions);
      console.log(`[${VERSION}] 👀 Observing video element`);
    }

    // ✅ AJOUT : Écouter les événements fullscreen pour forcer la mise à jour
    const handleFullscreenChange = () => {
      console.log(`[${VERSION}] 🎬 Fullscreen change detected, isFullscreen:`, !!(document.fullscreenElement));
      // Petit délai pour laisser le DOM se réorganiser
      setTimeout(() => {
        console.log(`[${VERSION}] ⏱️  First update (50ms delay)`);
        updateDimensions();
      }, 50);
      setTimeout(() => {
        console.log(`[${VERSION}] ⏱️  Second update (200ms delay)`);
        updateDimensions();
      }, 200);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    
    console.log(`[${VERSION}] 👂 Fullscreen listeners attached`);

    return () => {
      console.log(`[${VERSION}] 🧹 Cleaning up`);
      resizeObserver.disconnect();
      if (videoElement) {
        videoElement.removeEventListener('loadedmetadata', updateDimensions);
      }
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []); // Pas de dépendances - on observe les changements de taille directement



  const handleFullscreenRequest = () => {
    console.log("handleFullscreenRequest - Custom fullscreen");
  };
  
  const displayedAction = (() => {
    if (!enableAdvertisements) {
      return null;
    }

    if (!showAdPanel) {
      return null;
    }

    if (currentSalesActionRef?.current && showAdPanel.id === currentSalesActionRef.current.id) {
      return currentSalesActionRef.current;
    }
    
    return showAdPanel;
  })();

  const overlayContent = React.useMemo(() => {
    console.log('[1.3.11] 🎨 Recalculating overlayContent with:', {
      displayedAction: !!displayedAction,
      adWidth,
      marginHorizontal,
      marginVertical,
      adPanelPosition
    });
    
    if (!displayedAction) {
      console.log('[1.3.11] ❌ No displayedAction, returning null');
      return null;
    }

    return (
      <div 
        className={classNames(styles.adPanel, { 
          [styles.adPanelLeft]: adPanelPosition === 'left', 
          [styles.adPanelRight]: adPanelPosition === 'right' 
        })}
        style={{
          position: 'absolute',
          top: `${marginVertical}px`,
          left: adPanelPosition === 'left' ? `${marginHorizontal}px` : 'auto',
          right: adPanelPosition === 'right' ? `${marginHorizontal}px` : 'auto',
          bottom: `${marginVertical}px`,
          width: `${adWidth}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 10
        }}
        ref={(el) => {
          if (el) {
            console.log('[1.3.11] 🎨 Advertisement div rendered with style:', {
              top: `${marginVertical}px`,
              left: adPanelPosition === 'left' ? `${marginHorizontal}px` : 'auto',
              right: adPanelPosition === 'right' ? `${marginHorizontal}px` : 'auto',
              bottom: `${marginVertical}px`,
              width: `${adWidth}px`,
              adPanelPosition,
              currentAdWidth: adWidth,
              currentMarginH: marginHorizontal,
              currentMarginV: marginVertical
            });
          }
        }}
      >
        <Advertisement product          = {currentProduct}
                       selectedVariant  = {displayedAction ? displayedAction.productVariant   : undefined}
                       selectedImage    = {displayedAction ? displayedAction.productImage     : undefined}
                       width            = {adWidth}
                       onlyQRcode       = {displayedAction ? displayedAction.onlyQRcode       : undefined}
                       comment1         = {displayedAction ? displayedAction.comment1         : undefined}
                       comment2         = {displayedAction ? displayedAction.comment2         : undefined}
                       shapeColor1      = {displayedAction ? displayedAction.shapeColor1      : undefined}
                       shapeColor2      = {displayedAction ? displayedAction.shapeColor2      : undefined}
                       backgroundColor1 = {displayedAction ? displayedAction.backgroundColor1 : undefined}
                       backgroundColor2 = {displayedAction ? displayedAction.backgroundColor2 : undefined}
                       redirectionUuid  = {displayedAction ? displayedAction.redirectionUuid  : undefined} 
                       withImage        = {displayedAction ? displayedAction.withImage        : undefined} 
                       alternateImage   = {alternateImageUrl}
                       key              = {`${adUpdateCounter}-${adWidth}`} />
      </div>
    );
  }, [displayedAction, currentProduct, adWidth, marginHorizontal, marginVertical, adPanelPosition, alternateImageUrl, adUpdateCounter]);


  return (
  
    <div ref       = {containerRef}
         className = {classNames(styles.viewer, className)} >
      <VideoPlayer url                 = {url}
                   onReady             = {onReady}
                   onStart             = {onStart}
                   onError             = {onError}
                   onProgress          = {handleProgress}
                   onEnd               = {onEnd}
                   onPause             = {onPause}
                   onSeek              = {handleSeek}
                   onFullscreenRequest = {handleFullscreenRequest}
                   onGoToStart         = {handleGoToStart}
                   onGoToEnd           = {handleGoToEnd}
                   className           = {styles.video}
                   existingMarkers     = {existingMarkers}
                   initialMarker       = {currentMarker}
                   overlayContent      = {overlayContent} />
    </div>
  );
};

export default VideoPlayerWithAdvertisingSupport;
